/**
 * OnboardingsPageV2
 *
 * V2 variant of OnboardingsPage — lists onboardings and navigates to the
 * V2 editor route (/project/:pid/onboarding-v2/:oid) instead of opening
 * a modal overlay.
 *
 * Reuses shared V2-safe components: OnboardingsListView, CreateOnboardingWizardV2,
 * CreateFolderDialog, EditOnboardingDialog.
 *
 * Sprint: Schema V2 Migration - Sprint 1
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from '@frontend/contexts/AuthContext';
import { OnboardingsListView } from '../components/OnboardingsListView';
import { CreateOnboardingWizardV2 } from '../components/CreateOnboardingWizardV2';
import { CreateFolderDialog } from '../components/CreateFolderDialog';
import { EditOnboardingDialog } from '../components/EditOnboardingDialog';
import { OnboardingSettingsDrawer } from '../components/OnboardingSettingsDrawer';
import { useOnboardingSettingsParam } from '../hooks/useOnboardingSettingsParam';
import { useOnboardingActionsV2 } from '../hooks/useOnboardingActionsV2';
import { useEditOnboardingV2 } from '../hooks/useEditOnboardingV2';
import { useFolders } from '../hooks/useFolders';
import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import type { Onboarding, OnboardingVersion, OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import type { OnboardingPlatform } from '@modules/locale-config';
import { OnboardingListItem } from '../types/onboardings.types';
import { readOnboardingFolderMemory, writeOnboardingFolderMemory } from '../utils/folderMemory';
import { useGuideHighlight } from '@modules/guides/hooks/useGuideHighlight';

export function OnboardingsPageV2() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  useGuideHighlight();
  // Onboarding settings drawer: URL state `?settings[=fonts|locales]` (deep link, D-32).
  const onboardingSettings = useOnboardingSettingsParam();

  // Folder navigation state
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(() => (
    projectId ? readOnboardingFolderMemory(projectId).folderId : null
  ));
  const [currentFolderName, setCurrentFolderName] = useState<string | undefined>(undefined);

  // Dialog state
  const [createOnboardingOpen, setCreateOnboardingOpen] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [isFirstOnboarding, setIsFirstOnboarding] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOnboarding, setEditingOnboarding] = useState<Onboarding | null>(null);
  const [editingVersion, setEditingVersion] = useState<OnboardingVersion | null>(null);
  const [editLoading, setEditLoading] = useState(false);

  // Key to force refresh of OnboardingsListView
  const [refreshKey, setRefreshKey] = useState(0);

  // Services
  const onboardingServiceRef = useRef(new OnboardingService());

  // Actions hooks
  const { createOnboarding } = useOnboardingActionsV2({
    projectId: projectId || '',
    userId: currentUser?.uid || '',
  });

  const { updateOnboarding, loading: editSaving } = useEditOnboardingV2({
    projectId: projectId || '',
  });

  const { createFolder, folders, loading: foldersLoading } = useFolders({
    projectId: projectId || '',
  });

  useEffect(() => {
    if (!projectId) return;

    const storedFolder = readOnboardingFolderMemory(projectId);
    setCurrentFolderId(storedFolder.folderId);
    if (storedFolder.folderId === null) {
      setCurrentFolderName(undefined);
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId || foldersLoading) return;

    if (currentFolderId === null) {
      setCurrentFolderName(undefined);
      return;
    }

    const folder = folders.find(f => f.id === currentFolderId);
    if (folder) {
      setCurrentFolderName(folder.name);
      return;
    }

    setCurrentFolderId(null);
    setCurrentFolderName(undefined);
    writeOnboardingFolderMemory(projectId, null);
  }, [currentFolderId, folders, foldersLoading, projectId]);

  // Trigger list refresh
  const triggerRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  // Handlers
  const handleFolderClick = useCallback((folderId: string) => {
    setCurrentFolderId(folderId);
    const folder = folders.find(f => f.id === folderId);
    setCurrentFolderName(folder?.name || 'Folder');
    if (projectId) {
      writeOnboardingFolderMemory(projectId, folderId);
    }
  }, [folders, projectId]);

  // V2: Navigate to V2 editor route instead of opening modal
  const handleOnboardingClick = useCallback((onboardingId: string) => {
    navigate(`/project/${projectId}/onboarding-v2/${onboardingId}`);
  }, [navigate, projectId]);

  const handleNavigateToRoot = useCallback(() => {
    setCurrentFolderId(null);
    setCurrentFolderName(undefined);
    if (projectId) {
      writeOnboardingFolderMemory(projectId, null);
    }
  }, [projectId]);

  const handleOpenCreateOnboarding = useCallback(() => {
    setCreateOnboardingOpen(true);
  }, []);

  const handleOpenCreateFolder = useCallback(() => {
    setCreateFolderOpen(true);
  }, []);

  // V2: Navigate to V2 editor after creation
  const handleCreateOnboarding = useCallback(async (data: {
    name: string;
    defaultLanguage: string;
    languages: string[];
    platform: OnboardingPlatform;
    themeSettingsV2?: OnboardingThemeSettingsV2 | null;
  }): Promise<string> => {
    const onboardingId = await createOnboarding({
      name: data.name,
      defaultLanguage: data.defaultLanguage,
      languages: data.languages,
      platform: data.platform,
      themeSettingsV2: data.themeSettingsV2,
      folderId: currentFolderId,
    });
    setCreateOnboardingOpen(false);
    triggerRefresh();
    navigate(`/project/${projectId}/onboarding-v2/${onboardingId}`);
    return onboardingId;
  }, [createOnboarding, currentFolderId, navigate, projectId, triggerRefresh]);

  const handleCreateFolder = useCallback(async (name: string): Promise<unknown> => {
    await createFolder(name);
    setCreateFolderOpen(false);
    triggerRefresh();
    return null;
  }, [createFolder, triggerRefresh]);

  // Edit onboarding handlers
  const handleEditOnboarding = useCallback(async (item: OnboardingListItem) => {
    if (item.type !== 'onboarding' || !projectId) return;

    setEditLoading(true);
    try {
      const service = onboardingServiceRef.current;
      const onboarding = await service.getOnboarding(projectId, item.id);
      if (!onboarding) {
        console.error('Onboarding not found');
        return;
      }
      const latestVersion = await service.getLatestVersion(projectId, item.id);
      setEditingOnboarding(onboarding);
      setEditingVersion(latestVersion);
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Failed to load onboarding for editing:', error);
    } finally {
      setEditLoading(false);
    }
  }, [projectId]);

  const handleEditSave = useCallback(async (data: {
    name: string;
    defaultLanguage: string;
    languages: string[];
  }) => {
    if (!editingOnboarding) return;
    await updateOnboarding(editingOnboarding.id, data);
    setEditDialogOpen(false);
    setEditingOnboarding(null);
    setEditingVersion(null);
    triggerRefresh();
  }, [editingOnboarding, updateOnboarding, triggerRefresh]);

  const handleEditClose = useCallback(() => {
    setEditDialogOpen(false);
    setEditingOnboarding(null);
    setEditingVersion(null);
  }, []);

  // Bulk delete moved into OnboardingsListView (R9): header-checkbox
  // selection + batch bar own the flow; the old page-level BulkDeleteDialog
  // wiring is gone.

  if (!projectId || !currentUser) {
    return null;
  }

  return (
    <Box sx={{ height: '100%' }}>
      <OnboardingsListView
        key={refreshKey}
        projectId={projectId}
        userId={currentUser.uid}
        currentFolderId={currentFolderId}
        currentFolderName={currentFolderName}
        onFolderClick={handleFolderClick}
        onOnboardingClick={handleOnboardingClick}
        onNavigateToRoot={handleNavigateToRoot}
        onCreateOnboarding={handleOpenCreateOnboarding}
        onCreateFolder={handleOpenCreateFolder}
        onListChanged={triggerRefresh}
        onEmptyStateChange={setIsFirstOnboarding}
        onEditOnboarding={handleEditOnboarding}
        onOpenSettings={() => onboardingSettings.openSettings()}
      />

      {/* Onboarding settings (Font Mappings, Locale Configuration) — unmounted while closed */}
      <OnboardingSettingsDrawer
        projectId={projectId!}
        open={onboardingSettings.open}
        onClose={onboardingSettings.closeSettings}
        initialSection={onboardingSettings.section}
      />

      {/* Create Onboarding Wizard V2 (3-step with V2 theme selection) */}
      <CreateOnboardingWizardV2
        open={createOnboardingOpen}
        onClose={() => setCreateOnboardingOpen(false)}
        onCreate={handleCreateOnboarding}
        projectId={projectId!}
        folderId={currentFolderId}
        isFirstOnboarding={isFirstOnboarding}
      />

      {/* Create Folder Dialog */}
      <CreateFolderDialog
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        onCreate={handleCreateFolder}
      />

      {/* Edit Onboarding Dialog */}
      <EditOnboardingDialog
        open={editDialogOpen}
        onClose={handleEditClose}
        onSave={handleEditSave}
        onboarding={editingOnboarding}
        latestVersion={editingVersion}
        loading={editSaving || editLoading}
      />

      {/* V2 editor opens through route-based navigation */}
    </Box>
  );
}

export default OnboardingsPageV2;
